<template>
  <overlay-loader v-if="isLoading" />
  <div v-else id="app">
    <slot />
  </div>
</template>

<script setup lang="ts">
import AppManagementApi from "@apis/app.management.api";
import { userOnlineService } from "@/plugins/common/service/user.online.service.ts";
import DOMUtils from "@utils/dom.utils";
import { isDefined, isEmpty } from "@utils/var.check";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useStore } from "vuex";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant.ts";
import DeviceUtils from "@utils/device.utils.ts";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";
import AuthApi from "@apis/auth.api.ts";

// data
const resetGlobalActionButton = ref(null);
const isLoading = ref(true);

// store
const store = useStore();
const existingIntegratorAppList = store.getters[`${NAMESPACE.APP}/getIntegratorAppList`];

// computed
const isLogged = computed(() => store.getters[`${NAMESPACE.AUTH}/isLogged`]);

// methods
function hideOverflowMenuAndTooltips() {
  // close all opened overflow menu
  const openedMenu = DOMUtils.elOf(document.body, ".bx--overflow-menu-options--open");
  openedMenu?.forEach((menu) => DOMUtils.removeClass(menu, "bx--overflow-menu-options--open"));

  // close all opened tooltips
  const tooltips = DOMUtils.el(".bx--tooltip__trigger");
  if (!tooltips) {
    return;
  }
  //if there are more than one selector the value is in array form
  if (isDefined(tooltips.length)) {
    tooltips.forEach((tooltip) => {
      hideTooltipAction(tooltip);
    });
  } else {
    //if there is only one selector the value is in object form
    hideTooltipAction(tooltips);
  }
}

function hideTooltipAction(tooltip) {
  //Tooltips trigger button has two layer to the parent and use "dataVisible" to determine whether the tooltip is active
  if (
    isDefined(tooltip.parentElement.parentElement.__vue__) &&
    tooltip.parentElement.parentElement.__vue__.dataVisible
  ) {
    tooltip.parentElement.parentElement.__vue__.toggle();
  }
}

const handleOffline = () => {
  userActivityTrackEvent.connectionStatus({ action: "disconnect" });
  store.dispatch("app/setNetworkConnected", false);
};

const handleOnline = () => {
  store.dispatch("app/setNetworkConnected", true);
  if (store.getters["auth/isLogged"]) {
    AuthApi.pingSession().finally(() => {
      setTimeout(() => {
        userActivityTrackEvent.connectionStatus({ action: "reconnect" });
      }, 1000);
    });
  }
};

// lifecycle hooks
onMounted(() => {
  resetGlobalActionButton.value = setInterval(() => {
    const headerGlobalButton = DOMUtils.elOf(
      document,
      ".bx--header__global .cv-header-global-action",
    );

    if (headerGlobalButton.length > 0) {
      headerGlobalButton.forEach((btn) => {
        DOMUtils.removeClass(btn, "bx--btn");
        DOMUtils.removeClass(btn, "bx--btn--primary");
      });
    }
  });

  window.addEventListener("offline", handleOffline);
  window.addEventListener("online", handleOnline);

  userActivityTrackEvent.flushConnectionEvents();

  if (isLogged.value && existingIntegratorAppList == null) {
    // Check store first for integrator access to see if exist, otherwise API call
    const promiseArray = [];
    promiseArray.push(AppManagementApi.getIntegratorAppCategoryAccess());

    Promise.all(promiseArray)
      .then((response) => {
        if (!isEmpty(response) && !isEmpty(response[0])) {
          store.dispatch("app/setIntegratorAppList", response[0].data);
        }

        userOnlineService.sendHeartBeat();

        isLoading.value = false;
      })
      .catch(() => {
        isLoading.value = false;
      });
  } else {
    isLoading.value = false;
  }

  //auto hide tooltips content and overflow menu when scroll
  //use touchmove event for mobile for tablet, scroll for destop
  if (DeviceUtils.isMobile() || DeviceUtils.isTablet()) {
    document.querySelector("body").addEventListener(
      "touchmove",
      () => {
        hideOverflowMenuAndTooltips();
      },
      true,
    );
  } else {
    window.addEventListener(
      "scroll",
      () => {
        hideOverflowMenuAndTooltips();
      },
      true,
    );
  }
});

onUnmounted(() => {
  window.removeEventListener("offline", handleOffline);
  window.removeEventListener("online", handleOnline);

  if (DeviceUtils.isMobile() || DeviceUtils.isTablet()) {
    document.querySelector("body").removeEventListener(
      "touchmove",
      () => {
        hideOverflowMenuAndTooltips();
      },
      true,
    );
  } else {
    window.removeEventListener(
      "scroll",
      () => {
        hideOverflowMenuAndTooltips();
      },
      true,
    );
  }
});
</script>
